import {Icon} from '@atoms';
import {useModal} from '@hooks';
import {
  getIsLastNodeSelected,
  getIsLastSelectedWorkspaceReservationLocationLoaded,
  getIsLoadingDeskSuggestions,
  getIsSelectedBuildingWithoutFloors,
  getNodeTypeAndId,
  getSelectedBuildingName,
  getSelectedNodes,
  getSelectedSectionSubtitle,
  getSelectedSectionTitle,
  selectArea,
  selectDesks,
  selectFloor,
  selectLocationBasedOnBuildingId,
  useAppDispatch,
  useAppSelector,
} from '@lib/store';
import {OfficeWorkday, WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {dateKeyForDateTimeString} from '@lib/store/src/data/timeslots/dateTimeHelpers';
import {Attendee} from '@lib/store/src/data/timeslots/selectors/getAttendeeOverview';
import {getInitialWorkspaceSelection} from '@lib/store/src/data/timeslots/selectors/getInitialWorkspaceSelection';
import {getSortedConnectedUsersWithReservationOnDateInNode} from '@lib/store/src/data/timeslots/selectors/index';
import {AvatarStack, ModalCell, ModalCellButton, ModalCellItem, ModalCellItemIcon} from '@molecules';
import {WorkspaceSelectionCard} from '@organisms';
import {FlexCol, P, Span} from '@quarks';
import {pxToRem} from '@utils';
import {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useTheme} from 'styled-components';
import {StyledText} from '../BookWorkspaceCard/styles';
import {useWorkspaceHasSpaceForGroupBooking} from '@lib/store/src/ui/hooks/timeslots/useWorkspaceHasSpaceForGroupBooking';

export function SelectWorkspaceButton({
  workday,
  workspaceReservation,
  attendees = [],
  inviteeCount,
}: {
  workday: OfficeWorkday;
  workspaceReservation?: WorkspaceReservation;
  attendees?: Array<Attendee>;
  inviteeCount: number;
}) {
  const date = dateKeyForDateTimeString(workday.localStart);
  const {addPages} = useModal();
  const theme = useTheme();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const {buildingId, floorId, areaId, deskIds} = useAppSelector(getSelectedNodes);
  const lastSelectedWorkspaceLoaded = useAppSelector(getIsLastSelectedWorkspaceReservationLocationLoaded);

  const initialSelectedWorkspace = useAppSelector((state) =>
    getInitialWorkspaceSelection(state, {workday, workspaceReservation, members: attendees}),
  );

  const {
    buildingId: initialBuildingId,
    floorId: initialFloorId,
    areaId: initialAreaId,
    deskIds: initialDeskIds = [],
  } = initialSelectedWorkspace ?? {};

  const {
    nodeIds: [selectedNodeId],
    nodeType,
  } = getNodeTypeAndId(buildingId, floorId, areaId, deskIds);

  useEffect(
    function selectInitial() {
      if (initialSelectedWorkspace && lastSelectedWorkspaceLoaded) {
        if (!buildingId) {
          dispatch(selectLocationBasedOnBuildingId(initialBuildingId ?? ''));

          if (initialFloorId) {
            dispatch(selectFloor(initialFloorId ?? ''));
            dispatch(selectArea(initialAreaId ?? ''));
            dispatch(selectDesks(initialDeskIds ?? []));
          }
        }

        if (buildingId && buildingId === initialBuildingId && !floorId && initialFloorId) {
          dispatch(selectFloor(initialFloorId ?? ''));
          dispatch(selectArea(initialAreaId ?? ''));
          dispatch(selectDesks(initialDeskIds ?? []));
        }
      }
    },
    [
      buildingId,
      dispatch,
      initialAreaId,
      initialBuildingId,
      initialDeskIds,
      initialFloorId,
      initialSelectedWorkspace,
      lastSelectedWorkspaceLoaded,
      floorId,
    ],
  );

  const nodeId = areaId ?? floorId ?? buildingId;

  // TODO: This selector should handle buildings that support direct bookings without floors
  const defaultTitle = useAppSelector((state) => getSelectedSectionTitle(state, date));
  const fallbackTitle = useAppSelector(getSelectedBuildingName);
  const selectedNodeTitle = defaultTitle || fallbackTitle;

  const hasSpaceForGroupBooking = useWorkspaceHasSpaceForGroupBooking({
    workday,
    workspaceReservation,
    buildingId,
    floorId,
    areaId,
    inviteeCount,
  });

  const selectedNodeSubTitle = useAppSelector((state) => {
    if (!hasSpaceForGroupBooking) {
      return t('workplace:NotEnoughAvailableDesks');
    }

    const deskSubTitle = t('workplace:AnyAvailableDesk', {count: inviteeCount || 1});

    // TODO: this logic should handle building level bookings (now returns void)
    return getSelectedSectionSubtitle(state, deskSubTitle) || deskSubTitle;
  });

  const hasBookableSelection = useAppSelector(getIsLastNodeSelected);

  const selectedBuildingIsWithoutFloors = useAppSelector((state) => getIsSelectedBuildingWithoutFloors(state));

  // TODO: add to selector
  const isDisabled = useAppSelector(getIsLoadingDeskSuggestions);
  const locationCanBeChanged = !selectedBuildingIsWithoutFloors;

  const connectionSelectorProps = useMemo(() => ({dateKey: date, nodeId}), [date, nodeId]);
  const connections = useAppSelector((state) =>
    getSortedConnectedUsersWithReservationOnDateInNode(state, connectionSelectorProps),
  );

  if (!hasBookableSelection) {
    return (
      <ModalCellButton
        data-testid="select-workspace-button"
        onClick={() =>
          addPages([
            <WorkspaceSelectionCard
              date={date}
              workday={workday}
              inviteeCount={inviteeCount}
            />,
          ])
        }>
        <P color={theme.input['placeholder']}>{t('workplace:SelectWorkspace')}</P>
      </ModalCellButton>
    );
  }

  return (
    <ModalCell>
      <ModalCellItem
        alignItems="flex-start"
        data-testid="select-workspace-button"
        disabled={isDisabled || !locationCanBeChanged}
        left={
          <ModalCellItemIcon
            icon={'desk'}
            disabled={isDisabled}
          />
        }
        middle={
          <FlexCol
            gap={8}
            paddingTop={8}>
            <StyledText
              $disabled={isDisabled}
              fontWeight="bold">
              {selectedNodeTitle}
            </StyledText>
            <StyledText $disabled={isDisabled}>{selectedNodeSubTitle}</StyledText>
            <AvatarStack
              date={date}
              disabled={isDisabled}
              users={connections}
              tooltipId="SelectWorkspaceButton"
              overlap
            />
          </FlexCol>
        }
        right={
          locationCanBeChanged && (
            <Span padding={4}>
              <Icon
                size={pxToRem(24)}
                icon="arrowRight"
                aria-label={t('workplace:CheckRoomInfo')}
              />
            </Span>
          )
        }
        onClick={() =>
          !isDisabled &&
          locationCanBeChanged &&
          addPages([
            <WorkspaceSelectionCard
              date={date}
              workday={workday}
              inviteeCount={inviteeCount}
            />,
          ])
        }
      />
    </ModalCell>
  );
}
